// logger.js
class Logger {
  constructor() {
    // Get log level from URL params
    this.currentLevel = this.getLogLevelFromURL();
    
    // Bind methods to preserve context
    this.log = this.log.bind(this);
    this.error = this.error.bind(this);
    this.warn = this.warn.bind(this);
    this.info = this.info.bind(this);
    this.debug = this.debug.bind(this);
  }

  getLogLevelFromURL() {
    const params = new URLSearchParams(window.location.search);
    let level = parseInt(params.get('doot')) || parseInt(params.get('gostamp')) || 0;
    if (level == 0 && window.VITE_DEVELOPMENT) {
      level = 2;
      console.log("🔄 Logger Debug Level", level)
    }
    return Math.min(Math.max(level, 0), 5);
  }

  // Update log level dynamically
  updateLogLevel() {
    this.currentLevel = this.getLogLevelFromURL();
  }

  shouldLog(level) {
    // Always log errors regardless of importance level
    return this.isError || this.currentLevel >= level;
  }

  getCallerInfo() {
    const error = new Error();
    const stack = error.stack?.split('\n');
    
    // Find the first stack frame that's not from the logger
    for (let i = 3; i < stack.length; i++) {
      const line = stack[i];
      if (!line.includes('logger.js') && !line.includes('Logger.')) {
        // Extract component name and line number
        const match = line.match(/at\s+(?:.*\s+\()?(.+):(\d+):(\d+)/);
        if (match) {
          const [, file, lineNum, colNum] = match;
          const componentName = file.split('/').pop().replace(/\?.*$/, '');
          return {
            component: componentName,
            line: lineNum,
            column: colNum
          };
        }
      }
    }
    
    return { component: 'unknown', line: '?', column: '?' };
  }

  formatPrefix(method, level, callerInfo) {
    const methodLabel = method.toUpperCase();
    const levelLabel = level ? `L${level}` : '';
    const prefix = levelLabel ? `[${methodLabel}:${levelLabel}]` : `[${methodLabel}]`;
    // return `%c${prefix} ${callerInfo.component}:${callerInfo.line}`;
    return `%c${prefix} ${callerInfo.component}:${callerInfo.line}`;
  }

  getLogStyle(method) {
    const styles = {
      error: 'color: #ff0000; font-weight: bold;', // Red
      warn: 'color: #ff9800; font-weight: bold;',  // Orange
      info: 'color: #2196f3;',                      // Blue
      debug: 'color: #4caf50;',                     // Green
      log: 'color: #9e9e9e;'                        // Gray
    };
    return styles[method] || '';
  }

  logWithLevel(method, level, ...args) {
    // Set flag for error method
    this.isError = method === 'error';
    if (!this.shouldLog(level)) {
      this.isError = false;
      return;
    }

    const callerInfo = this.getCallerInfo();
    const prefix = this.formatPrefix(method, level, callerInfo);
    const style = this.getLogStyle(method);
    
    // Use appropriate console method
    const consoleMethod = console[method] || console.log;
    
    // Performance optimization: use console.group for better readability at high log levels
    if (this.currentLevel >= 4 && args.length > 2) {
      console.group(prefix, style);
      consoleMethod(...args);
      console.groupEnd();
    } else {
      // Simpler output for lower log levels or fewer arguments
      consoleMethod(prefix, style, ...args);
    }
    
    this.isError = false;
  }

  // Public methods with level parameter
  log(level, ...args) {
    this.logWithLevel('log', level, ...args);
  }

  error(level, ...args) {
    this.logWithLevel('error', level, ...args);
  }

  warn(level, ...args) {
    this.logWithLevel('warn', level, ...args);
  }

  info(level, ...args) {
    this.logWithLevel('info', level, ...args);
  }

  debug(level, ...args) {
    // console.log("🔄 logger debug", level, ...args);
    this.logWithLevel('debug', level, ...args);
  }
}

// Create singleton instance
const logger = new Logger();

// Listen for URL changes to update log level
if (typeof window !== 'undefined') {
  window.addEventListener('popstate', () => logger.updateLogLevel());
}

export default logger;

// Vue 3 Plugin with global injection
export const LoggerPlugin = {
  install(app, options = {}) {
    // Make logger available globally in Vue components
    app.config.globalProperties.$log = logger;
    
    // Also provide as an injectable
    app.provide('logger', logger);
    
    // IMPORTANT: Inject logger methods into globalProperties for direct access
    // This allows using log.error(), log.info() etc without importing
    app.config.globalProperties.log = {
      log: logger.log,
      error: logger.error,
      warn: logger.warn,
      info: logger.info,
      debug: logger.debug
    };
    
    // Make it available on window for global access (useful for debugging)
    if (typeof window !== 'undefined') {
      window.log = app.config.globalProperties.log;
    }
    
    // Optional: Add a global error handler
    if (options.captureErrors) {
      app.config.errorHandler = (err, vm, info) => {
        logger.error(1, 'Vue Error:', err, { info });
        // Forward to Sentry if available
        if (window.Sentry && window.Sentry.captureException) {
          window.Sentry.captureException(err, {
            contexts: {
              vue: { info }
            }
          });
        }
      };
    }
  }
};

// Composable for Composition API (if needed)
export function useLogger() {
  return logger;
}